import React from "react"
import { Button, Text, TextField } from "@quorumus/plum"
import {
    useCustomDescriptionQuery,
    useCreateCustomDescription,
    useUpdateCustomDescription,
} from "@/pages/ai-bill-tracker/hooks/useCustomDescription"
import classes from "./CustomDescription.module.css"

interface CustomDescriptionProps {
    billId: number
    isEditing: boolean
    onEditingChange: (isEditing: boolean) => void
    text: string
    onTextChange: (text: string) => void
}

interface MetadataDisplayProps {
    lastTextUpdatedUser?: { first_name: string; last_name: string }
    lastUser?: { first_name: string; last_name: string }
    textLastUpdated?: string
    updated?: string
}

const MetadataDisplay: React.FC<MetadataDisplayProps> = ({
    lastTextUpdatedUser,
    lastUser,
    textLastUpdated,
    updated,
}) => {
    if (!lastTextUpdatedUser && !lastUser) {
        return null
    }

    const user = lastTextUpdatedUser || lastUser
    const timestamp = textLastUpdated || updated

    const userName = `${user.first_name} ${user.last_name}`.trim()
    const date = new Date(timestamp)
    const formattedDate = date.toLocaleDateString("en-US", {
        month: "long",
        day: "numeric",
        year: "numeric",
    })
    const formattedTime = date.toLocaleTimeString("en-US", {
        hour: "numeric",
        minute: "2-digit",
        hour12: true,
    })

    return (
        <Text size="sm">
            Updated by <strong>{userName}</strong> on{" "}
            <strong>
                {formattedDate} at {formattedTime}
            </strong>
        </Text>
    )
}

export const CustomDescription: React.FC<CustomDescriptionProps> = ({
    billId,
    isEditing,
    onEditingChange,
    text,
    onTextChange,
}) => {
    const { data: customDescription, isLoading } = useCustomDescriptionQuery(billId)
    const createMutation = useCreateCustomDescription()
    const updateMutation = useUpdateCustomDescription()

    const handleCancel = () => {
        // Reset to saved text
        onTextChange(customDescription?.text || "")
        onEditingChange(false)
    }

    const handleSave = async () => {
        try {
            if (customDescription?.id) {
                // Update existing
                await updateMutation.mutateAsync({
                    descriptionId: customDescription.id,
                    billId,
                    text,
                })
            } else {
                // Create new
                await createMutation.mutateAsync({
                    billId,
                    text,
                })
            }
            onEditingChange(false)
        } catch (error) {
            console.error("Failed to save custom description:", error)
        }
    }

    const isSaving = createMutation.isLoading || updateMutation.isLoading
    const hasTextChanged = text !== (customDescription?.text || "")

    if (isLoading) {
        return (
            <div className={classes.container}>
                <Text variant="body-sm" color="secondary">
                    Loading...
                </Text>
            </div>
        )
    }

    if (isEditing) {
        return (
            <div className={classes.editSection}>
                <Text variant="body-md" fw={500}>
                    Add a Custom Description
                </Text>
                <TextField
                    label=""
                    className={classes.textarea}
                    value={text}
                    onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => onTextChange(e.target.value)}
                    placeholder="e.g., Notes on sponsor's position or potential impact"
                    minRows={8}
                    multiline
                    resizable
                />
                <div className={classes.actions}>
                    <Button variant="secondary" onClick={handleCancel} disabled={isSaving}>
                        Cancel
                    </Button>
                    <Button
                        variant="primary"
                        onClick={handleSave}
                        disabled={isSaving || !hasTextChanged}
                        loading={isSaving}
                        data-plum-id="save-description"
                    >
                        Save
                    </Button>
                </div>
            </div>
        )
    }

    // View mode (when custom description exists and not editing)
    if (customDescription?.text) {
        return (
            <div className={classes.container}>
                <MetadataDisplay
                    lastTextUpdatedUser={customDescription.last_text_updated_user}
                    lastUser={customDescription.last_user}
                    textLastUpdated={customDescription.text_last_updated}
                    updated={customDescription.updated}
                />
                <div className={classes.header}>
                    <Text variant="body-sm" style={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
                        {customDescription.text}
                    </Text>
                </div>
            </div>
        )
    }

    return null
}
